import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { IS_PROD } from '@/constants';
import { googleTagManager, hotjar, mixpanel } from '@/configs/analytics-scripts';
import { CookieConsentValue } from '@/store/slices/cookie-consent.slice';

// Fire `marketing_site_viewed` once per browser session — matches the BI spec
// (anonymous user lands on the unauthenticated domain). Captures any UTM
// params in the URL so the dashboard's Top Acquisition Sources tile can
// attribute traffic. Bracketed Mixpanel name + snake_case dataLayer name
// follows the existing `[AUTH COMPLETED]` / `auth_completed` convention in
// use-auth-flow.hook.ts.
function trackMarketingSiteViewedOnce(): void {
	try {
		if (typeof sessionStorage === 'undefined') return;
		if (sessionStorage.getItem('msv_fired') === '1') return;
		const url = new URL(window.location.href);
		const props = {
			utm_source: url.searchParams.get('utm_source'),
			utm_medium: url.searchParams.get('utm_medium'),
			utm_campaign: url.searchParams.get('utm_campaign'),
			pathname: url.pathname,
			referrer: document.referrer || null,
		};
		// Mixpanel is loaded by the snippet just above; its stub queues calls
		// until init completes, so this is safe to call immediately.
		(window as any).mixpanel?.track?.('[MARKETING SITE VIEWED]', props);
		(window as any).dataLayer?.push({ event: 'marketing_site_viewed', ...props });
		sessionStorage.setItem('msv_fired', '1');
	} catch {
		// Tracking failures should never break the app.
	}
}

// Fire `email_link_clicked` when the user arrives at the app via an email link.
// Detected by utm_medium='email' (the standard convention used by our WC game
// emails). Deduped per browser session so the user isn't re-attributed on every
// SPA navigation that briefly retains the UTM in the URL.
function trackEmailLinkClickedOnce(): void {
	try {
		if (typeof sessionStorage === 'undefined') return;
		if (sessionStorage.getItem('elc_fired') === '1') return;
		const url = new URL(window.location.href);
		const utm_medium = url.searchParams.get('utm_medium');
		// Match exact 'email' or anything with 'email' prefix (e.g., 'email_digest').
		if (!utm_medium || !/^email(_|$)/.test(utm_medium)) return;
		const props = {
			utm_source: url.searchParams.get('utm_source'),
			utm_medium,
			utm_campaign: url.searchParams.get('utm_campaign'),
			utm_content: url.searchParams.get('utm_content'),
			landing_path: url.pathname,
		};
		(window as any).mixpanel?.track?.('[EMAIL LINK CLICKED]', props);
		(window as any).dataLayer?.push({ event: 'email_link_clicked', ...props });
		sessionStorage.setItem('elc_fired', '1');
	} catch {
		// Tracking failures should never break the app.
	}
}

export function useInitAnalytics(): void {
	const consentValue = useSelector((state: any) => state.cookieConsent.value) as CookieConsentValue;

	useEffect(() => {
		if (IS_PROD && consentValue) {
			mixpanel();
			hotjar();
			googleTagManager();
			trackMarketingSiteViewedOnce();
			trackEmailLinkClickedOnce();
		}
	}, [consentValue]);
}
